import React from 'react';
import cx from 'classnames';
import { ToastPosition, TypeOptions } from 'react-toastify';
import StepManagement from './StepManagement';
import FieldList from './FieldList';
import { FormField, ContactFormConfig } from '../../../../types/ContactForm';
import styles from './FieldsTab.module.scss';
import Button from '../../../../components/Button';
import { validateSteps } from '../../../../helpers/stepValidation';
import { getFieldsForStep, getFieldsCount } from '../../../../helpers/fieldHelpers';
import { useFieldsTabState } from '../../../../hooks/useFieldsTabState';
import { useStepOperations } from '../../../../hooks/useStepOperations';
import { useOptionOperations } from '../../../../hooks/useOptionOperations';
import { ValidationInfoBox } from './ValidationInfoBox';
import { InfoBox } from './InfoBox';
import { SimpleStepHeader } from './SimpleStepHeader';

interface FieldsTabProps {
    config: ContactFormConfig;
    updateConfig: (property: string, value: any) => void;
    updateField: (index: number, property: string, value: any) => void;
    addField: (step?: number) => void;
    removeField: (index: number) => void;
    moveField: (index: number, direction: 'up' | 'down') => void;
    showModal: (data: any) => void;
    hideModal: () => void;
    showToast: (position: ToastPosition, type: TypeOptions, message: string) => void;
    saveAllSettings: () => Promise<void>;
}

const FieldsTab: React.FC<FieldsTabProps> = ({
    config,
    updateConfig,
    updateField,
    addField,
    removeField,
    moveField,
    showModal,
    hideModal,
    showToast,
    saveAllSettings
}) => {
    const {
        activeStep,
        setActiveStep,
        focusedOption,
        setFocusedOption,
        justCreatedOption,
        setJustCreatedOption,
        showMultistepUI,
        setShowMultistepUI,
        showDescription,
        setShowDescription
    } = useFieldsTabState({ config, updateConfig });

    const {
        addStep,
        removeStep,
        updateStepTitle,
        updateStepDescription
    } = useStepOperations({ config, updateConfig, activeStep, setActiveStep });

    const {
        addOption,
        removeOption,
        updateOption,
        cleanupEmptyOptions
    } = useOptionOperations({ 
        config, 
        updateField, 
        setFocusedOption, 
        setJustCreatedOption,
        justCreatedOption 
    });

    const getFieldsForStepLocal = (stepNumber: number): Array<{ field: FormField, originalIndex: number }> => {
        return getFieldsForStep(config.formFields, stepNumber);
    };

    const getFieldsCountLocal = (stepNumber: number): number => {
        return getFieldsCount(config.formFields, stepNumber);
    };

    const addFieldToCurrentStep = (): void => {
        addField(activeStep);
    };

    const moveFieldInStep = (fieldsInStep: Array<{
        field: FormField,
        originalIndex: number
    }>, fieldIndex: number, direction: 'up' | 'down'): void => {
        if (direction === 'up' && fieldIndex > 0) {
            const currentField = fieldsInStep[fieldIndex];
            moveField(currentField.originalIndex, 'up');
        } else if (direction === 'down' && fieldIndex < fieldsInStep.length - 1) {
            const currentField = fieldsInStep[fieldIndex];
            moveField(currentField.originalIndex, 'down');
        }
    };

    const steps = config.steps || [];
    const fieldsInCurrentStep = getFieldsForStepLocal(activeStep);
    const isSingleStep = steps.length === 1;
    const shouldShowMultistepUI = showMultistepUI || !isSingleStep;

    const validationResult = validateSteps(config);

    const enableMultistepMode = (): void => {
        addStep();
        setShowMultistepUI(true);
    };

    const handleSave = async (): Promise<void> => {
        if (!validationResult.isValid) {
            showToast('top-right', 'error', 'Bitte fügen Sie mindestens ein Feld zu jedem Schritt hinzu, bevor Sie speichern.');
            return;
        }
        await saveAllSettings();
    };

    return (
        <div className={cx(styles.container)}>
            <InfoBox 
                isSingleStep={isSingleStep}
                showMultistepUI={showMultistepUI}
                shouldShowMultistepUI={shouldShowMultistepUI}
            />

            <ValidationInfoBox validationResult={validationResult} />

            {!isSingleStep || showMultistepUI ? (
                <StepManagement
                    steps={steps}
                    activeStep={activeStep}
                    fieldsCount={getFieldsCountLocal}
                    onStepChange={setActiveStep}
                    onAddStep={addStep}
                    onRemoveStep={removeStep}
                    onUpdateStepTitle={updateStepTitle}
                    onUpdateStepDescription={updateStepDescription}
                    showModal={showModal}
                    hideModal={hideModal}
                    showToast={showToast}
                    showDescription={showDescription}
                    setShowDescription={setShowDescription}
                    validationResult={validationResult}
                >
                    <FieldList
                        fieldsInCurrentStep={fieldsInCurrentStep}
                        allFields={config.formFields}
                        onUpdateField={updateField}
                        onRemoveField={removeField}
                        onMoveFieldInStep={moveFieldInStep}
                        onAddFieldToCurrentStep={addFieldToCurrentStep}
                        onUpdateOption={updateOption}
                        onAddOption={addOption}
                        onRemoveOption={removeOption}
                        onCleanupEmptyOptions={cleanupEmptyOptions}
                        saveAllSettings={handleSave}
                    />
                </StepManagement>
            ) : (
                <>
                    <SimpleStepHeader
                        step={steps[0]}
                        showDescription={showDescription}
                        onToggleDescription={() => setShowDescription(!showDescription)}
                        onUpdateTitle={(title) => updateStepTitle(1, title)}
                        onUpdateDescription={(description) => updateStepDescription(1, description)}
                    />

                    <div className={cx(styles['step-content'])}>
                        <FieldList
                            fieldsInCurrentStep={fieldsInCurrentStep}
                            allFields={config.formFields}
                            onUpdateField={updateField}
                            onRemoveField={removeField}
                            onMoveFieldInStep={moveFieldInStep}
                            onAddFieldToCurrentStep={addFieldToCurrentStep}
                            onUpdateOption={updateOption}
                            onAddOption={addOption}
                            onRemoveOption={removeOption}
                            onCleanupEmptyOptions={cleanupEmptyOptions}
                            saveAllSettings={handleSave}
                        />
                    </div>
                </>
            )}

            {isSingleStep && !showMultistepUI && config.formFields.length > 0 && (
                <div className={cx(styles['enable-multistep-container'])}>
                    <Button
                        hierarchy="secondary"
                        text="Schritt hinzüfugen"
                        fn={enableMultistepMode}
                        classes={styles['enable-multistep-btn']}
                    />
                </div>
            )}
        </div>
    );
};

export default FieldsTab; 